import axios from 'axios'
import { toast } from 'react-hot-toast'

// Helper function to display validation errors
const displayValidationErrors = (title, errors) => {
  if (!errors || errors.length === 0) {
    toast.error(title || 'Validation failed')
    return
  }
  
  const errorMessages = errors.map(err => {
    const field = err.path || err.param || err.field || ''
    const message = err.message || err.msg || 'Invalid value'
    return field ? `${field}: ${message}` : message
  })
  
  // Create a formatted error message
  const formattedMessage = `${title || 'Validation Failed'}\n\n${errorMessages.map(msg => `• ${msg}`).join('\n')}`
  
  toast.error(formattedMessage, {
    duration: 6000,
    style: {
      maxWidth: '500px',
      whiteSpace: 'pre-line',
      textAlign: 'left'
    }
  })
}

// Create axios instance
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8001/api-v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    // Priority: 1) Already set on defaults (from setAuthToken), 2) Redux persist, 3) direct localStorage
    let token = apiClient.defaults.headers.common['Authorization']?.replace('Bearer ', '') || null
    
    if (!token) {
      try {
        const persistedState = localStorage.getItem('persist:eduna-root')
        if (persistedState) {
          const parsed = JSON.parse(persistedState)
          if (parsed.auth) {
            const authState = JSON.parse(parsed.auth)
            token = authState.token
          }
        }
      } catch (error) {
        console.error('Error reading token from storage:', error)
      }
    }
    
    // Fallback to direct localStorage check
    if (!token) {
      token = localStorage.getItem('eduna-token')
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration for debugging
    const duration = new Date() - response.config.metadata.startTime
    console.log(`API Request: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`)
    
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Handle network errors
    if (!error.response) {
      toast.error('Network error. Please check your connection.')
      return Promise.reject(error)
    }
    
    const { status, data } = error.response
    
    // Handle different error status codes
    switch (status) {
      case 400:
        // Bad request - similar to validation errors
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          displayValidationErrors(data?.message || 'Bad Request', data.errors)
        } else {
          toast.error(data?.message || 'Bad request.')
        }
        break
        
      case 401:
        // Unauthorized - token expired or invalid
        if (!originalRequest._retry) {
          originalRequest._retry = true
          
          try {
            // Try to refresh token
            const refreshToken = localStorage.getItem('eduna-refresh-token')
            if (refreshToken) {
              const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:8001/api-v1'}/auth/refresh`, {
                refreshToken
              })
              
              if (response.data.success) {
                const { token, refreshToken: newRefreshToken } = response.data.data
                
                // Update stored tokens
                localStorage.setItem('eduna-token', token)
                localStorage.setItem('eduna-refresh-token', newRefreshToken)
                
                // Update authorization header
                apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
                originalRequest.headers['Authorization'] = `Bearer ${token}`
                
                // Retry original request
                return apiClient(originalRequest)
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
          }
          
          // If refresh fails, clear tokens and redirect to login
          localStorage.removeItem('eduna-token')
          localStorage.removeItem('eduna-refresh-token')
          delete apiClient.defaults.headers.common['Authorization']
          
          // Only show toast if not already on login page
          if (!window.location.pathname.includes('/login')) {
            toast.error('Session expired. Please login again.')
            window.location.href = '/login'
          }
        }
        break
        
      case 403:
        // Forbidden - insufficient permissions
        toast.error(data?.message || 'You do not have permission to perform this action.')
        break
        
      case 404:
        // Not found
        toast.error(data?.message || 'Resource not found.')
        break
        
      case 422:
        // Validation error - DON'T show toast here, let the component handle it
        // This prevents duplicate toasts
        break
        
      case 429:
        // Rate limit exceeded
        toast.error('Too many requests. Please try again later.')
        break
        
      case 500:
        // Internal server error
        if (data?.message) {
          toast.error(`Server Error: ${data.message}`, { duration: 5000 })
        } else {
          toast.error('Server error. Please try again later.')
        }
        break
        
      default:
        // Other errors - show detailed message if available
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          displayValidationErrors(data?.message || 'Error', data.errors)
        } else {
          toast.error(data?.message || 'An unexpected error occurred.')
        }
    }
    
    return Promise.reject(error)
  }
)

// Helper methods
apiClient.setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    localStorage.setItem('eduna-token', token)
  }
}

apiClient.clearAuthToken = () => {
  delete apiClient.defaults.headers.common['Authorization']
  localStorage.removeItem('eduna-token')
  localStorage.removeItem('eduna-refresh-token')
}

// Request/Response logging for development
if (import.meta.env.DEV) {
  apiClient.interceptors.request.use(request => {
    console.log('Starting Request:', {
      method: request.method?.toUpperCase(),
      url: request.url,
      data: request.data,
      headers: request.headers
    })
    return request
  })
  
  apiClient.interceptors.response.use(
    response => {
      console.log('Response:', {
        status: response.status,
        data: response.data,
        url: response.config.url
      })
      return response
    },
    error => {
      console.error('Response Error:', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        message: error.message
      })
      return Promise.reject(error)
    }
  )
}

export default apiClient